export type SolutionAssetBulkDigitalTwinsData = {
  data: SolutionAssetBulkDigitalTwinsItem[];
  loading?: boolean;
};

export type SolutionAssetBulkDigitalTwinsItem = {
  id: string;
  relationshipName: string;
  siteId?: string;
  label?: string;
  level: number;
  properties: Record<string, any>;
  icon?: React.ReactNode;
  children: SolutionAssetBulkDigitalTwinsItem[];
};

export type SolutionAssetBulkDigitalTwinsRootItem = {
  root: SolutionAssetBulkDigitalTwinsItem;
};

export type SolutionAssetEdgeDetailsItem = {
  uId: string;
  twinId?: string;
  relationshipName?: string;
  properties: {
    name?: string;
    schema?: string;
    writable?: boolean;
  };
  enterprise: string;
  site?: string;
  area?: string;
  workCenter?: string;
  workUnit?: string;
};

export type SolutionAssetEdgeDetailItem = {
  data: SolutionAssetEdgeDetailsItem;
};

export type SolutionAssetTwin = {
  twinId?: string;
  modelId?: string;
};

export enum SolutionAssetTwinField {
  twinId = 'twinId',
  modelId = 'modelId',
}

export type SolutionAssetConfigListItem = {
  id: string;
  solutionId: string;
  twinId: string;
  fileName: string;
  fileId?: string;
  size?: string;
  mimeType?: string;
  updatedOn: string;
  userId: string;
  userEmail: string;
};

export type SolutionAssetConfig = {
  data: SolutionAssetConfigListItem[];
  totalItems: number;
};

export enum SolutionAssetConfigListItemField {
  id = 'id',
  fileName = 'fileName',
  updatedOn = 'updatedOn',
  userId = 'userId',
  fileId = 'fileId',
  size = 'size',
  mimeType = 'mimeType',
  userEmail = 'userEmail',
}

export enum SolutionAssetHierarchyType {
  Properties = 'properties',
  Configurations = 'configurations',
  AssetModel = 'assetModel',
  MappingConfiguration = 'mappingConfiguration',
  ADX_Dashboard = 'adxDashboard',
  Dashboard = 'dashboard',
  Digital_Twin = 'digitalTwin',
  HMI = 'hmi',
  HMIConfiguration = 'hmiConfiguration',
}

export enum SolutionDigitalTwinType {
  Omniverse = 'Omniverse',
  LiveMonitor = 'Live Monitor',
  DataSimulation = 'Synthetic Data Generation',
}

export type SolutionAssetModelListItem = {
  id: string;
  solutionId: string;
  twinId: string;
  fileName: string;
  fileId: string;
  size: string;
  mimeType: string;
  updatedOn: string;
  userId: string;
  userEmail: string;
};

export type SolutionAssetModel = {
  data: SolutionAssetModelListItem[];
};

export type SolutionMappingConfigItem = {
  id: string;
  sourceType: string;
  destinationType: string;
  path: string;
  mappings: any[];
  solutionId: string;
  siteId: string;
  twinId: string;
  tableName?: string;
};

export type SolutionMapConfig = {
  data: SolutionMappingConfigItem[];
};

export enum SolutionMappingListItemField {
  sourceType = 'sourceType',
  destinationType = 'destinationType',
  path = 'path',
  sourceProperty = 'sourceProperty',
  destinationProperty = 'destinationProperty',
  mappings = 'mappings',
  tableName = 'tableName',
}

export enum SolutionMappingFilterField {
  sourceProperty = 'sourceProperty',
  destinationProperty = 'destinationProperty',
}

export enum SolutionMappingSourceType {
  UniversalEdge = 'Universal_Edge',
}

export enum SolutionMappingConfigType {
  Omniverse = 'Omniverse',
  AzureDigitalTwin = 'Azure Digital Twin',
}

export type SolutionAssetDashboardItem = {
  name: string;
  dashboardId: string;
  description?: string;
};

export enum SolutionAssetDashboardField {
  Name = 'name',
  DashboardId = 'dashboardId',
  Description = 'description',
}

export type DashboardDataSource = {
  type: 'apiInternal' | 'apiExternal' | 'database' | 'file';
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  endpoint: string;
  headers: Record<string, string>[];
  body: string;
  refreshRate: number;
};

export type ChartAxis = {
  columnName: string;
  label: string;
};

export type DataSeries = {
  columnName: string;
  label: string;
};

export type ChartConfiguration = {
  xAxis: ChartAxis;
  yAxis: ChartAxis;
  dataSeries: DataSeries[];
  showLegend: boolean;
};

export type WidgetLayout = {
  width: number;
  height: number;
  order: number;
};

export type DashboardWidget = {
  id: string;
  title: string;
  chartType: string;
  dataSource: DashboardDataSource;
  chartConfiguration: ChartConfiguration;
  layout: WidgetLayout;
  createdAt: string;
  lastModifiedAt: string;
  createdBy: string;
  lastModifiedBy: string;
};

export type DashboardItem = {
  id: string;
  name: string;
  description: string;
  solutionId: string;
  createdAt: string;
  lastModifiedAt: string;
  createdBy: string;
  lastModifiedBy: string;
  twinId?: string;
  widgets?: DashboardWidget[];
};

export type DashboardListResponse = {
  data: DashboardItem[];
};

export type SolutionAssetDashboardComponentField = {
  id: string;
  solutionId: string;
  dashboardSummary: SolutionAssetDashboardItem;
};

export type assetDashboardSummaryList = {
  data: SolutionAssetDashboardItem[];
};

export enum DashboardField {
  Id = 'id',
  Name = 'name',
  Description = 'description',
  SolutionId = 'solutionId',
  CreatedAt = 'createdAt',
  LastModifiedAt = 'lastModifiedAt',
  CreatedBy = 'createdBy',
  LastModifiedBy = 'lastModifiedBy',
  IsDeleted = 'isDeleted',
  Widgets = 'widgets',
}

export enum WidgetField {
  Id = 'id',
  Title = 'title',
  ChartType = 'chartType',
  DataSource = 'dataSource',
  ChartConfiguration = 'chartConfiguration',
  Layout = 'layout',
  CreatedAt = 'createdAt',
  LastModifiedAt = 'lastModifiedAt',
  CreatedBy = 'createdBy',
  LastModifiedBy = 'lastModifiedBy',
}

export enum ChartType {
  Line = 'line',
  Area = 'area',
  Bar = 'bar',
  Column = 'column',
  Pie = 'pie',
  Scatter = 'scatter',
  radar = 'radar',
  Table = 'table',
}

export enum DataSourceType {
  ApiInternal = 'apiInternal',
  ApiExternal = 'apiExternal',
  Database = 'database',
  File = 'file',
}

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

export interface DataSeriesField {
  columnName: string;
  label: string;
}

export interface ChartAxisField {
  columnName: string;
  label: string;
}

export interface DataSourceField {
  type: string;
  method: string;
  endpoint: string;
  headers: { key: string; value: string }[];
  body: string;
  refreshRate: number;
}

export interface ChartConfigurationField {
  xAxis: ChartAxisField;
  yAxis: ChartAxisField;
  dataSeries: DataSeriesField[];
  showLegend: boolean;
  selectedColumns?: DataSeriesField[]; // For table widgets
}

export interface LayoutField {
  width: number;
  height: number;
  order: number;
}
